<template>
  <section id="inbox-placement_accounts">
    <card-widget class="my-2">
      <template #cardBody>
        <v-row class="d-flex justify-end" no-gutters>
          <v-col md="4" cols="12">
            <h6 class="text-h6 font-weight-large secondary--font">Accounts</h6>
          </v-col>
          <v-col cols="12" md="8" class="d-flex align-center justify-end">
            <div class="ml-4">
              <debounce-search-text-field
                prop-to-search="name"
                placeholder="Search Accounts..."
                :lazy-search="getAccountsList"
                :sort-by="sortConfig.sortBy"
                :sort-order="sortConfig.sortOrder"
                :no-of-records="accountParams.per_page"
                @search-value="accountSearch = $event"
              />
            </div>
            <div id="add-account-btn" class="ml-4">
              <v-btn
                text
                outlined
                class="
                  mx-1
                  text--secondary
                  btn-light-grey-border
                  font-weight-bold
                "
                :to="{ name: 'add-account', params: { customerId } }"
              >
                <v-icon>mdi-plus</v-icon>
                Add Account
              </v-btn>
            </div>
          </v-col>
        </v-row>
        <v-row class="d-flex justify-center pt-3" no-gutters>
          <v-col cols="12" class="pt-3">
            <customer-accounts-table
              :data="filteredAccounts"
              :headers="headers"
              :is-loading="isLoading || areStatsLoading"
              @get-records="getAccountsList"
              default-sort-order="id"
            />
          </v-col>
        </v-row>
      </template>
    </card-widget>
  </section>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { CardWidget } from "@/components/widgets";
import { accountsHeaders as headers } from "@/data/table-headers";
import { DebounceSearchTextField } from "@/components/shared";
import { TitleMixin, SharedUtilsMixin } from "@/mixins";

import { CustomerAccountsTable } from "@/components/tables";

import cloneDeep from "lodash/cloneDeep";
import { getAccountUsage } from "@/services";
import { MONTHS, PROMISE_STATUSES } from "@/constants";
import { isEmpty, isType, hasProp } from "@/utils/common.utils";
import dayjs from "dayjs";

/**
 * Accounts index page
 */
export default {
  name: "Accounts",
  /**
   *   Page title
   */
  title: "Accounts",
  /**
   * -------------- Mixin -------------
   */
  mixins: [TitleMixin, SharedUtilsMixin],
  /**
   *  ---------------- Components ------------------
   */
  components: {
    CardWidget,
    CustomerAccountsTable,
    DebounceSearchTextField,
  },
  /**
   *  ---------------- Data properties ---------------
   */
  data() {
    return {
      headers,
      isLoading: false,
      errorMessage: null,
      hasError: false,
      accountParams: {},
      accountSearch: "",
      areStatsLoading: false,
      filteredAccounts: [],
      inboxPlacementMonths: null,
      accountsInboxStats: null,
      cachedInboxStats: null,
    };
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    isEmpty,
    ...mapActions({
      getAccounts: "account/getAccounts",
    }),
    ...mapMutations({
      setAccounts: "account/SET_ACCOUNTS",
    }),
    /**
     * @description Computes a date into month and year format
     * @param {Date} date Date to be used for Month and year format
     * @example Oct 2021
     */
    getMonthYear(date) {
      const localDate = dayjs(date).utc();
      return `${this.getMonth(localDate.month())} ${localDate.year()}`;
    },
    /**
     * @returns name of the month on the basis of date month index value
     */
    getMonth(month) {
      return MONTHS.month_names_short[month];
    },
    /**
     * @description Computes name of the month for a date
     * @param {Date} date Date whose month is to be computed
     * @returns {String} Month name
     */
    getInboxMonth(date) {
      date = dayjs(date).utc();
      return this.getMonth(date.month()).toLowerCase();
    },
    /**
     * Send multiple http request to account Usage endpoint
     * to fetch inbox placement month records of all the customer accounts
     */
    async getAccountsInboxStats() {
      try {
        this.areStatsLoading = true;
        const promises = this.accounts.map((account) =>
          this.fetchAccountUsageList(account)
        );
        this.accountsInboxStats = await Promise.allSettled(promises);

        this.inboxPlacementMonths = cloneDeep(this.accountsInboxStats).sort(
          (a, b) => a.value.length - b.value.length
        )[this.accountsInboxStats.length - 1]?.value;

        if (this.isComputeTableHeaders)
          this.cachedInboxStats = this.inboxPlacementMonths;

        await this.computeTableHeaders();
        this.getFilteredAccounts();
      } finally {
        this.areStatsLoading = false;
      }
    },
    /**
     * Updates customer accounts headers array annd inserts Month name and year
     * for a inbox records of an acccount
     */
    computeTableHeaders() {
      if (this.isComputeTableHeaders) return;

      const computedHeaders = this.inboxPlacementMonths.map(({ month }) => ({
        text: this.getMonthYear(month),
        value: this.getInboxMonth(month),
        class: "body-2 text--secondary py-5",
        sortable: false,
      }));

      this.headers = cloneDeep(headers);
      this.headers.splice(3, 0, ...computedHeaders);
    },
    /**
     * Fetches list of inbox placements for an account for 6 months
     */
    async fetchAccountUsageList({ id }) {
      try {
        this.isLoading = true;
        const { data } = await getAccountUsage(id);
        return data;
      } catch {
        return [];
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Sets isLoading state property of the component
     * @param {Boolean} val Boolean value of the isLoading property
     */
    setIsLoading(val) {
      if (!isType(val, "boolean")) return;
      this.isLoading = val;
    },
    /**
     * Dispatches action in store and fetches accounts list from the api
     */
    async getAccountsList(params = this.accountParams) {
      try {
        this.setIsLoading(true);
        params["q[customer_id_eq]"] = this.customerId;
        if (this.accountSearch && !hasProp(params, "q[name_cont]")) {
          params["q[name_cont]"] = this.accountSearch;
        }
        this.accountParams = params;

        await this.getAccounts(params);
        this.getAccountsInboxStats();
      } catch (error) {
        this.errorhandler(error.message, true);
      } finally {
        this.setIsLoading(false);
      }
    },
    /**
     * Handles all the  api's errors occured while consuming them
     */
    errorhandler(errorMessage, hasError) {
      this.errorMessage = errorMessage;
      this.hasError = hasError;
    },
    /**
     * Computes and maps account inbox placement stats as key value pair in their object
     * records
     */
    computesAccountInboxStats(status, data) {
      if (!isEmpty(data) && status === PROMISE_STATUSES.fulfilled) {
        return data.reduce((acc, { count, month }) => {
          const inboxMonthDate = this.getInboxMonth(month);
          acc[inboxMonthDate] = count ?? 0;
          return acc;
        }, {});
      } else {
        return !isEmpty(this.inboxPlacementMonths)
          ? this.inboxPlacementMonths.reduce((acc, { month }) => {
              acc[this.getInboxMonth(month)] = 0;
              return acc;
            }, {})
          : {};
      }
    },
    /**
     * Filteres Accounts with 6 month inbox placement stats
     */
    async getFilteredAccounts() {
      try {
        if (isEmpty(this.accounts)) return (this.filteredAccounts = []);
        this.areStatsLoading = true;

        const filteredAccounts = this.accountsInboxStats.map(
          ({ status, value }, i) => ({
            ...this.accounts[i],
            ...this.computesAccountInboxStats(status, value),
          })
        );

        this.filteredAccounts = filteredAccounts;
      } finally {
        this.areStatsLoading = false;
      }
    },
  },
  /**
   *  ---------------- Computed properties ------------------
   */
  computed: {
    ...mapGetters({
      accounts: "account/accounts",
    }),
    /**
     * @description Boolean value determining that table headers need to be computed or not
     * @type {Boolean}
     */
    isComputeTableHeaders() {
      return (
        isEmpty(this.inboxPlacementMonths) && !isEmpty(this.cachedInboxStats)
      );
    },
    /**
     * Get Customer ID
     */
    customerId() {
      return this.$route.params?.id;
    },

    /**
     * Customers listing sorting configuration
     */
    sortConfig() {
      return this.getSortConfig("accountParams", "name");
    },
  },
  /**
   * Sets accounts as empty array before route is changed
   */
  beforeDestroy() {
    this.setAccounts([]);
  },
};
</script>

<style lang="scss" scoped>
.search-accounts.v-input::v-deep .v-input__control .v-input__slot {
  min-height: 36px !important;
}
</style>
